import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

type Props = {
  value?: any[];
  accept?: string;
  onChange?: (files: any[]) => void;
  maxSize?: number;
  maxFiles?: number;
};

function FileUploader({
  value = [],
  accept,
  onChange = () => {},
  maxSize = Infinity,
  maxFiles = Infinity,
}: Props) {
  const { t } = useTranslation();

  const [files, setFiles] = useState<File[]>([]);

  // Synchroniser les fichiers sélectionnés avec la valeur passée en props
  useEffect(() => {
    if (value?.length) {
      setFiles(value);
    } else {
      setFiles([]);
    }
  }, [value]);

  // Gestion de l'ajout de fichiers
  const handleFileSelected = (selectedFiles: File[]) => {
    const validFiles = selectedFiles.filter(
      (file) => file.size <= maxSize && files.length < maxFiles
    );
    setFiles((prev) => [...prev, ...validFiles]);
    onChange([...files, ...validFiles]); // Notifie les fichiers sélectionnés
  };

  // Gestion du dropzone pour sélectionner des fichiers
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileSelected,
    multiple: false,
    accept: (accept || undefined) as any,
  });

  // Suppression d'un fichier
  const handleRemoveFile = useCallback(
    (index: number) => {
      const updatedFiles = files.filter((_, i) => i !== index);
      setFiles(updatedFiles);
      onChange(updatedFiles); // Notifie les fichiers mis à jour
    },
    [files, onChange]
  );

  // Liste des fichiers sélectionnés
  const fileList = useMemo(
    () =>
      files.map((file, index) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className="flex items-center justify-between py-1 border-b"
        >
          <span>{file.name}</span>
          <button
            onClick={() => handleRemoveFile(index)}
            className="text-red-500 hover:underline"
            type="submit"
          >
            {t("actions.delete")}
          </button>
        </div>
      )),
    [files, handleRemoveFile, t]
  );

  return (
    <div className="max-w-md mx-auto">
      {/* Zone de drop */}
      <div
        {...getRootProps()}
        className="border-dashed border-2 border-gray-300 p-4 text-center rounded-md cursor-pointer"
      >
        <input {...getInputProps()} />
        <p>{t("import.fileUploaderDescription")}</p>
      </div>

      {/* Liste des fichiers */}
      {files.length > 0 && (
        <div className="mt-4">
          <h4 className="text-lg font-semibold">{t("import.selectedFiles")}</h4>
          <div>{fileList}</div>
        </div>
      )}
    </div>
  );
}

export default FileUploader;
